import { ResponsiveSankey } from '@nivo/sankey';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface FinancialData {
  revenue?: string | number | null;
  grossProfit?: string | number | null;
  operatingIncome?: string | number | null;
  netIncome?: string | number | null;
}

interface Props {
  data: FinancialData;
  period?: string;
}

// Parse formatted currency string to number (e.g., "$66.6B" -> 66.6)
const parseCurrency = (value?: string | number | null): number => {
  if (!value) return 0;

  // If already a number, convert to billions
  if (typeof value === 'number') {
    return value / 1_000_000_000;
  }

  // If not a string, return 0
  if (typeof value !== 'string') return 0;

  // Remove $ and commas
  const clean = value.replace(/[$,]/g, '').trim();

  // Handle B for billions, M for millions
  if (clean.includes('B') || clean.includes('b')) {
    return parseFloat(clean.replace(/[Bb]/g, ''));
  } else if (clean.includes('M') || clean.includes('m')) {
    return parseFloat(clean.replace(/[Mm]/g, '')) / 1000; // Convert to billions
  } else {
    // Small numbers, convert to billions
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num / 1_000_000_000;
  }
};

export default function FinancialFlowSankey({ data, period }: Props) {
  // Parse all values to numbers (in billions)
  const revenue = parseCurrency(data.revenue);
  const grossProfit = parseCurrency(data.grossProfit);
  const operatingIncome = parseCurrency(data.operatingIncome);
  const netIncome = parseCurrency(data.netIncome);

  // Calculate derived values
  const cogs = revenue - grossProfit; // Cost of Goods Sold
  const operatingExpenses = grossProfit - operatingIncome;
  const otherExpensesAndTaxes = operatingIncome - netIncome;

  // Check if we have enough data
  if (revenue === 0 && grossProfit === 0 && netIncome === 0) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-neutral-900">
            Financial Flow {period && `- ${period}`}
          </h3>
          <p className="text-sm text-neutral-600 mt-1">
            Revenue breakdown through to net income
          </p>
        </CardHeader>
        <CardBody>
          <div className="text-center py-12 text-neutral-500">
            <p>Insufficient financial data for flow visualization</p>
            <p className="text-sm mt-2">Need revenue, gross profit, and net income data</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Build Sankey data structure
  // Format: { nodes: [...], links: [...] }
  const sankeyData = {
    nodes: [
      { id: 'Revenue', nodeColor: '#3b82f6' },
      { id: 'COGS', nodeColor: '#ef4444' },
      { id: 'Gross Profit', nodeColor: '#10b981' },
      { id: 'Operating Expenses', nodeColor: '#f59e0b' },
      { id: 'Operating Income', nodeColor: '#14b8a6' },
      { id: 'Other & Taxes', nodeColor: '#f59e0b' },
      { id: 'Net Income', nodeColor: '#22c55e' },
    ],
    links: [
      // Revenue splits into COGS and Gross Profit
      { source: 'Revenue', target: 'COGS', value: Math.max(cogs, 0.01) },
      { source: 'Revenue', target: 'Gross Profit', value: Math.max(grossProfit, 0.01) },

      // Gross Profit splits into Operating Expenses and Operating Income
      { source: 'Gross Profit', target: 'Operating Expenses', value: Math.max(operatingExpenses, 0.01) },
      { source: 'Gross Profit', target: 'Operating Income', value: Math.max(operatingIncome, 0.01) },

      // Operating Income splits into Other/Taxes and Net Income
      { source: 'Operating Income', target: 'Other & Taxes', value: Math.max(otherExpensesAndTaxes, 0.01) },
      { source: 'Operating Income', target: 'Net Income', value: Math.max(netIncome, 0.01) },
    ],
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">
              Revenue Flow Analysis
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              How revenue flows through to net income
            </p>
          </div>
          {period && (
            <span className="text-xs font-medium text-neutral-400 bg-neutral-50 px-2.5 py-1 rounded-full">
              {period}
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <div style={{ height: '420px' }}>
          <ResponsiveSankey
            data={sankeyData}
            margin={{ top: 20, right: 160, bottom: 20, left: 160 }}
            align="justify"
            colors={{ scheme: 'category10' }}
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.35}
            nodeThickness={18}
            nodeSpacing={24}
            nodeBorderWidth={0}
            nodeBorderColor={{ from: 'color', modifiers: [['darker', 0.8]] }}
            nodeBorderRadius={3}
            linkOpacity={0.5}
            linkHoverOthersOpacity={0.1}
            linkContract={3}
            enableLinkGradient={true}
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={16}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
            animate={true}
            motionConfig="gentle"
          />
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-neutral-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Revenue', value: revenue, color: 'text-primary-600', dotColor: 'bg-blue-500' },
              { label: 'Gross Profit', value: grossProfit, color: 'text-green-600', dotColor: 'bg-green-500' },
              { label: 'Operating Income', value: operatingIncome, color: 'text-teal-600', dotColor: 'bg-teal-500' },
              { label: 'Net Income', value: netIncome, color: 'text-green-500', dotColor: 'bg-green-400' },
            ].map(({ label, value: val, color, dotColor }) => (
              <div key={label} className="flex items-center gap-2.5">
                <div className={`w-3 h-3 rounded-full ${dotColor} shrink-0`} />
                <div>
                  <div className="text-xs font-medium text-neutral-500">{label}</div>
                  <div className={`text-lg font-bold ${color} tabular-nums`}>
                    {val === 0 ? 'N/A' : `$${Math.abs(val).toFixed(1)}B`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
